import { Injectable } from '@angular/core';

/**
 * Platform seam for the interactive Entra sign-in flow (WI #31089, Ryn's Roost
 * mobile). `base/auth` is platform-agnostic, but the interactive flow needs three
 * primitives that only exist per platform:
 *   - `randomBytes` / `sha256`: PKCE needs a cryptographically-secure verifier and
 *     an S256 challenge. NativeScript exposes NO Web Crypto (`crypto.subtle` /
 *     `getRandomValues` are absent), so a platform must supply real crypto.
 *   - `authorize`: open a system browser / ASWebAuthenticationSession at the
 *     /authorize URL and return the final redirect URL carrying `?code&state`.
 *
 * A platform registers an override via Angular DI:
 *   `{ provide: InteractiveAuthProvider, useClass: NativeScriptInteractiveAuthProvider }`
 * (NS impl lives in `libs/xplat/nativescript/utils`). Web apps use MSAL directly
 * and never register one.
 *
 * The base implementation THROWS rather than returning a weak fallback: an
 * insecure `Math.random` verifier or a silently skipped browser step would
 * degrade the security of the flow invisibly, so a missing platform provider must
 * fail loudly at first use.
 */
@Injectable({ providedIn: 'root' })
export class InteractiveAuthProvider {
  /** `length` cryptographically-secure random bytes. */
  randomBytes(_length: number): Uint8Array {
    throw new Error('InteractiveAuthProvider.randomBytes is not implemented on this platform');
  }

  /** SHA-256 digest of a UTF-8 string, as raw bytes. */
  sha256(_input: string): Promise<Uint8Array> {
    return Promise.reject(
      new Error('InteractiveAuthProvider.sha256 is not implemented on this platform')
    );
  }

  /**
   * Open the system browser at `authorizeUrl`, and resolve with the final
   * redirect URL (`redirectUri` + `?code&state`). Reject on user-cancel or error.
   */
  authorize(_authorizeUrl: string, _redirectUri: string): Promise<string> {
    return Promise.reject(
      new Error('InteractiveAuthProvider.authorize is not implemented on this platform')
    );
  }
}
